import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, updateDoc, collection, query, orderBy, limit, onSnapshot, serverTimestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Client Dashboard sources (client profile card)
//
// Where the monthly client dashboard (dashboard.core6.marketing) gets each
// tab's data for THIS client. Saved on clients/{id}:
//   dashboardSources.gbpLocationId     Windsor google_my_business account id
//                                      ("locations/123…") → GBP + Reviews tabs
//   windsorAdsAccountId                Windsor google_ads account ("123-456-7890")
//                                      — shared with the WhatConverts ads pull
//   dashboardSources.metaAdsAccountId  Windsor facebook account id → Meta Ads tab
//   dashboardSources.gbpPullDay        day of month the GBP pull runs (default 3)
//   dashboardSources.gscEnabled        true → Google Search tab (Search Console,
//                                      through the Hub's own GSC connection; no id
//                                      needed; skipped if the client is gscDisabled)
//   dashboardSources.brandTerms        extra words that mark a search as branded
//                                      (nicknames, owner names, old names); the
//                                      business name and domain always count
//   dashboardSources.trackingNumberMap { "<CallScaler number name>": channel }
//                                      — decides each call's channel before the
//                                      name rules; "website" = use CallScaler's
//                                      own tag for that number (number-swap lines)
// A tab appears for the client only when its source is filled in. Number names
// come from the latest Clarity run (clarityReports metrics.trackingNumbers).
// ---------------------------------------------------------------------------

const CHANNEL_OPTIONS = [
  { value: '', label: '— not mapped (name rules decide) —' },
  { value: 'website', label: 'Website number (use CallScaler\'s tag)' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'meta_ads', label: 'Meta Ads' },
  { value: 'gbp', label: 'Google Business Profile' },
  { value: 'organic', label: 'Organic Search' },
  { value: 'direct', label: 'Direct' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 13,
  border: '1px solid var(--border-color, #d6d9de)', borderRadius: 6, background: 'var(--bg-primary, #fff)', color: 'inherit',
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.3px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 };

function SourceBadge({ on, label }) {
  // Filled vs outlined + text, never hue alone.
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap', marginRight: 6,
      ...(on
        ? { background: 'var(--text-primary)', color: 'var(--bg-primary, #fff)', border: '1px solid var(--text-primary)' }
        : { background: 'transparent', color: 'var(--text-muted)', border: '1px dashed var(--text-muted)' }),
    }}>
      {on ? '● ' : '○ '}{label}
    </span>
  );
}

export default function DashboardSourcesPanel({ clientId, client }) {
  const saved = client?.dashboardSources || {};
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(client?.dashboardEnabled === true);
  const [gbpLocationId, setGbpLocationId] = useState(saved.gbpLocationId || '');
  const [gbpExtra, setGbpExtra] = useState((saved.gbpLocationIds || []).join('\n'));
  const [googleAdsId, setGoogleAdsId] = useState(client?.windsorAdsAccountId || '');
  const [metaAdsId, setMetaAdsId] = useState(saved.metaAdsAccountId || '');
  const [gbpPullDay, setGbpPullDay] = useState(saved.gbpPullDay || 3);
  const [gscEnabled, setGscEnabled] = useState(saved.gscEnabled === true);
  const [brandTerms, setBrandTerms] = useState((saved.brandTerms || []).join('\n'));
  const [chatCap, setChatCap] = useState(saved.chatMonthlyCap || 150);
  const [numberMap, setNumberMap] = useState(saved.trackingNumberMap || {});
  const [newName, setNewName] = useState('');
  const [seenNumbers, setSeenNumbers] = useState({});
  const [latestPeriod, setLatestPeriod] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Re-sync the form when the client doc changes under us (another staff save).
  useEffect(() => {
    const s = client?.dashboardSources || {};
    setEnabled(client?.dashboardEnabled === true);
    setGbpLocationId(s.gbpLocationId || '');
    setGbpExtra((s.gbpLocationIds || []).join('\n'));
    setGoogleAdsId(client?.windsorAdsAccountId || '');
    setMetaAdsId(s.metaAdsAccountId || '');
    setGbpPullDay(s.gbpPullDay || 3);
    setGscEnabled(s.gscEnabled === true);
    setBrandTerms((s.brandTerms || []).join('\n'));
    setChatCap(s.chatMonthlyCap || 150);
    setNumberMap(s.trackingNumberMap || {});
  }, [client?.dashboardSources, client?.windsorAdsAccountId, client?.dashboardEnabled]);

  // Tracking numbers seen in the latest Clarity run.
  useEffect(() => {
    const q = query(collection(db, 'clients', clientId, 'clarityReports'), orderBy('period', 'desc'), limit(1));
    const unsub = onSnapshot(q, snap => {
      const d = snap.docs[0]?.data();
      setSeenNumbers((d && d.metrics && d.metrics.trackingNumbers) || {});
      setLatestPeriod(d ? d.period : null);
    }, () => setSeenNumbers({}));
    return () => unsub();
  }, [clientId]);

  const allNames = [...new Set([...Object.keys(seenNumbers), ...Object.keys(numberMap)])]
    .sort((a, b) => (seenNumbers[b] || 0) - (seenNumbers[a] || 0) || a.localeCompare(b));
  const unmapped = allNames.filter(n => !numberMap[n]);

  const setMapping = (name, value) => {
    setNumberMap(m => {
      const next = { ...m };
      if (value) next[name] = value; else delete next[name];
      return next;
    });
  };

  const addName = (e) => {
    e.preventDefault();
    const n = newName.trim();
    if (!n) return;
    setMapping(n, 'website');
    setNewName('');
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const day = Math.min(28, Math.max(1, Number(gbpPullDay) || 3));
      await updateDoc(doc(db, 'clients', clientId), {
        dashboardEnabled: enabled,
        windsorAdsAccountId: googleAdsId.trim() || null,
        // Keep everything this card does not edit (per-location config, AI
        // Answers questions, …) — the whole map is written back.
        dashboardSources: {
          ...(client?.dashboardSources || {}),
          gbpLocationId: gbpLocationId.trim() || null,
          gbpLocationIds: gbpExtra.split(/[\n,]/).map(x => x.trim()).filter(x => x && x !== gbpLocationId.trim()),
          metaAdsAccountId: metaAdsId.trim() || null,
          gbpPullDay: day,
          gscEnabled,
          brandTerms: brandTerms.split(/[\n,]/).map(x => x.trim()).filter(Boolean),
          chatMonthlyCap: Math.max(1, Math.min(2000, Number(chatCap) || 150)),
          autoPublishHours: client?.dashboardSources?.autoPublishHours || null,
          trackingNumberMap: numberMap,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || null,
        },
      });
      setMsg({ ok: true, text: 'Saved. The map applies from the next Clarity run; re-run a month to re-attribute it.' });
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Could not save.' });
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { label: 'Calls', on: !!client?.callscaler_api_key },
    { label: 'Forms', on: (client?.clarityFormIds || []).length > 0 || !!client?.jobtreadGrantKey },
    { label: 'Reviews', on: !!gbpLocationId.trim() },
    { label: 'GBP', on: !!gbpLocationId.trim() },
    { label: 'Google Ads', on: !!googleAdsId.trim() },
    { label: 'Meta Ads', on: !!metaAdsId.trim() },
    { label: 'Google Search', on: gscEnabled && client?.gscDisabled !== true && !!client?.clientUrl },
  ];

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Dashboard Sources</div>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
            {client?.dashboardEnabled === true ? 'ON · ' : 'OFF · '}
            {tabs.filter(t => t.on).length} of {tabs.length} tabs
            {unmapped.length ? ` · ${unmapped.length} number${unmapped.length === 1 ? '' : 's'} unmapped` : ''}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
            Where the client dashboard gets each tab's numbers. A tab only shows for the client when its source is filled in.
            Account ids come from Windsor (Google Business Profile, Google Ads, Meta Ads connectors).
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            Monthly dashboard pipeline on for this client
          </label>

          <div style={{ marginBottom: 14 }}>
            {tabs.map(t => <SourceBadge key={t.label} on={t.on} label={t.label} />)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Windsor GBP location id</label>
              <input style={inputStyle} value={gbpLocationId} onChange={e => setGbpLocationId(e.target.value)} placeholder="locations/1234567890" />
            </div>
            <div>
              <label style={labelStyle}>Additional GBP locations (one per line)</label>
              <textarea style={{ ...inputStyle, minHeight: 56 }} value={gbpExtra} onChange={e => setGbpExtra(e.target.value)} placeholder="locations/… for other offices; metrics are summed, reviews pooled" />
            </div>
            <div>
              <label style={labelStyle}>GBP pull day of month</label>
              <input style={inputStyle} type="number" min={1} max={28} value={gbpPullDay} onChange={e => setGbpPullDay(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Windsor Google Ads account</label>
              <input style={inputStyle} value={googleAdsId} onChange={e => setGoogleAdsId(e.target.value)} placeholder="123-456-7890" />
            </div>
            <div>
              <label style={labelStyle}>Windsor Meta Ads account</label>
              <input style={inputStyle} value={metaAdsId} onChange={e => setMetaAdsId(e.target.value)} placeholder="act_1234567890" />
            </div>
            <div>
              <label style={labelStyle}>Chat questions per month (cap)</label>
              <input style={inputStyle} type="number" min={1} max={2000} value={chatCap} onChange={e => setChatCap(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Google Search tab (Search Console)</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginTop: 6 }}>
                <input type="checkbox" checked={gscEnabled} onChange={e => setGscEnabled(e.target.checked)} />
                Show clicks, impressions and top branded / non-branded searches
              </label>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {client?.gscDisabled
                  ? 'Search Console is disconnected for this client (profile), so the tab stays off until it is reconnected.'
                  : 'Uses the Hub\'s Search Console connection and this client\'s site URL. Pulled on the 4th (Google\'s data lags three days).'}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Brand words (one per line)</label>
              <textarea style={{ ...inputStyle, minHeight: 56 }} value={brandTerms} onChange={e => setBrandTerms(e.target.value)} placeholder={'Nicknames, owner names, old names, misspellings\ne.g. "Dr. Smith", "DW"'} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>A search counts as branded when it contains the business name, the domain, or one of these.</div>
            </div>
          </div>

          <div style={{ ...labelStyle, marginTop: 6 }}>
            Tracking numbers → channel {latestPeriod ? <span style={{ fontWeight: 400, textTransform: 'none' }}>(names from the {latestPeriod} Clarity run)</span> : null}
          </div>
          {allNames.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>No tracking numbers seen yet. Run a Clarity report first, or add a name below.</div>
          )}
          {allNames.map(name => (
            <div key={name} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 13 }}>
                {name}
                {seenNumbers[name] ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> · {seenNumbers[name]} calls</span> : null}
                {!numberMap[name] ? <span style={{ fontSize: 11, fontWeight: 700, marginLeft: 6 }}>UNMAPPED</span> : null}
              </div>
              <select style={inputStyle} value={numberMap[name] || ''} onChange={e => setMapping(name, e.target.value)}>
                {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setMapping(name, '')} title="Remove mapping">✕</button>
            </div>
          ))}
          <form onSubmit={addName} style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 14 }}>
            <input style={inputStyle} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Add a number name exactly as CallScaler shows it" />
            <button type="submit" className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }}>Add</button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save sources'}</button>
            {msg && <span style={{ fontSize: 12, fontWeight: 600 }}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
